var _excluded = ["centered", "children", "clickToChange", "infinite", "onSlideIndexChange", "partialVisible", "pagination", "paginationLocation", "responsive", "showArrows", "showPagination", "slideIndex", "slidesPerPage", "slidesPerScroll", "classes"];
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { useInView } from 'react-intersection-observer';
import MultiCarousel from '@lululemon/react-multi-carousel';
import { getClones } from '@lululemon/react-multi-carousel/lib/utils';
import '@lululemon/react-multi-carousel/lib/styles.css';
require("./carousel.css");
var styles = {
  "wrapper": "wrapper-wzSG8",
  "wrapperReverse": "wrapperReverse-J3XPN",
  "dots": "dots-1WBPg",
  "dot": "dot-1cT4d",
  "dotInner": "dotInner-q7U0x",
  "dotActive": "dotActive-rhTPX",
  "carouselItem": "carouselItem-2lbap"
};
import CarouselButton from '../common/carousel-button';
import Dot from './carousel-dots';
function useDragging(children) {
  var draggingRef = React.useRef(false);
  var afterChange = React.useCallback(() => {
    draggingRef.current = false;
  }, [draggingRef]);
  var beforeChange = React.useCallback(() => {
    draggingRef.current = true;
  }, [draggingRef]);
  var clonedChildren = React.useMemo(() => {
    var childrenArray = React.Children.toArray(children);
    return childrenArray.map((child, index) => {
      var _child$props, _child$props2;
      var onClick = (_child$props = child.props) === null || _child$props === void 0 ? void 0 : _child$props.onClick;
      var className = (_child$props2 = child.props) === null || _child$props2 === void 0 ? void 0 : _child$props2.className;
      return /*#__PURE__*/React.cloneElement(child, {
        className: cn(className, styles.carouselItem),
        ariaLabel: "slide ".concat(index + 1, " of ").concat(childrenArray.length),
        onClick: event => {
          if (draggingRef.current) {
            event.preventDefault();
            return;
          }
          if (onClick) {
            onClick(event);
          }
        }
      });
    });
  }, [children]);
  return {
    afterChange,
    beforeChange,
    clonedChildren
  };
}

// Disable draggable property for each image inside the carousel.
// Draggable property causes a poor drag scrolling experience in react-multi-carousel.
// This doesn't work for infinite mode because the carousel adds empty extra children.
var useDisableDraggableImgs = (componentInView, children) => {
  React.useLayoutEffect(() => {
    var imgs = document.querySelectorAll('.react-multi-carousel-list img');
    if (!imgs.length) return;
    var disableDraggable = img => img.setAttribute('draggable', false);
    imgs.forEach(disableDraggable);
  }, [componentInView, children]);
};

// % operator in javascript does not handle negative numbers
function modulo(n, m) {
  return (n % m + m) % m;
}
var Carousel = _ref => {
  var {
      centered,
      children,
      clickToChange,
      infinite,
      onSlideIndexChange,
      partialVisible,
      pagination,
      paginationLocation,
      responsive,
      showArrows,
      showPagination,
      slideIndex,
      slidesPerPage,
      slidesPerScroll,
      classes
    } = _ref,
    rest = _objectWithoutProperties(_ref, _excluded);
  var [containerRef, inView] = useInView({
    triggerOnce: true
  });
  useDisableDraggableImgs(inView, children);
  var carouselRef = React.useRef();
  var infiniteIndexOffset = children.length - (children.length - slidesPerPage * 2); // matches value set in react-multi-carousel/src/utils/clones.ts

  var {
    afterChange,
    beforeChange,
    clonedChildren
  } = useDragging(children);
  var getActualSlideIndex = useCallback(internalSlideIndex => {
    if (!infinite) {
      return internalSlideIndex;
    }

    // internal slideIndex state contains extra slides for infinite mode
    return modulo(internalSlideIndex - infiniteIndexOffset, clonedChildren.length);
  }, [infinite, clonedChildren, infiniteIndexOffset]);
  var getInternalSlideIndex = useCallback(actualSlideIndex => {
    if (!infinite) {
      return actualSlideIndex;
    }

    // internal index is offset for additional slides
    return modulo(slideIndex + infiniteIndexOffset, carouselRef.current.state.totalItems);
  }, [infinite, infiniteIndexOffset, slideIndex]);
  var handleAfterChange = React.useCallback((_, ref) => {
    afterChange();

    // If in controlled mode, pass the current slide to the callback
    if (onSlideIndexChange) {
      onSlideIndexChange(getActualSlideIndex(ref.currentSlide));
    }
  }, [afterChange, onSlideIndexChange, getActualSlideIndex]);

  // Monitor controlled slideIndex and update underlying component if out-of-sync.
  React.useEffect(() => {
    if (!carouselRef.current) return;
    if (infinite) {
      // internal index is offset for additional slides
      var intendedInternalSlideIndex = getInternalSlideIndex(slideIndex);
      var originalSlide = clonedChildren[slideIndex];
      var clones = getClones(carouselRef.current.state.slidesToShow, clonedChildren);

      // get matching cloned slides in the internal set of slides
      var matchingClonedSlideIndices = clones.map((c, i) => originalSlide.key === c.key ? i : '').filter(String); // filters out empty strings

      // if the slide it is on currently matches a clone, then no need to update
      if (matchingClonedSlideIndices.includes(carouselRef.current.state.currentSlide)) {
        return;
      }
      carouselRef.current.goToSlide(intendedInternalSlideIndex);
    } else if (carouselRef.current.state.currentSlide !== slideIndex) {
      carouselRef.current.goToSlide(slideIndex);
    }
  }, [carouselRef, slideIndex, clonedChildren, infinite, infiniteIndexOffset, getInternalSlideIndex]);
  var responsiveOptions = React.useMemo(() => {
    return responsive || {
      mobile: {
        breakpoint: {
          max: Number.MAX_SAFE_INTEGER,
          min: 0
        },
        items: slidesPerPage,
        slidesToSlide: slidesPerScroll
      }
    };
  }, [responsive, slidesPerPage, slidesPerScroll]);
  return /*#__PURE__*/React.createElement("div", {
    ref: containerRef,
    className: cn(styles.wrapper, {
      [styles.wrapperReverse]: paginationLocation === 'top'
    }, classes.wrapper)
  }, /*#__PURE__*/React.createElement(MultiCarousel, _extends({
    afterChange: handleAfterChange,
    arrows: showArrows,
    beforeChange: beforeChange,
    centerMode: centered,
    containerClass: classes.carousel,
    customDot: /*#__PURE__*/React.createElement(Dot, {
      pagination: pagination,
      className: classes.pagination,
      totalItems: clonedChildren.length
    }),
    customLeftArrow: /*#__PURE__*/React.createElement(CarouselButton, {
      direction: "left",
      className: classes.leftArrow
    }),
    customRightArrow: /*#__PURE__*/React.createElement(CarouselButton, {
      direction: "right",
      className: classes.rightArrow
    }),
    deviceType: "mobile",
    dotListClass: styles.dots,
    focusOnSelect: clickToChange,
    infinite: infinite,
    keyBoardControl: false,
    partialVisible: partialVisible,
    ref: carouselRef,
    renderDotsOutside: true,
    responsive: responsiveOptions,
    showDots: showPagination,
    ssr: true
  }, rest), clonedChildren));
};
Carousel.propTypes = {
  /** centers the current active slide */
  centered: PropTypes.bool,
  children: PropTypes.node,
  /** can overwrite any arrows styles. By default, arrows will disappear if
   the first or last slide is reached */
  classes: PropTypes.shape({
    wrapper: PropTypes.string,
    carousel: PropTypes.string,
    pagination: PropTypes.string,
    leftArrow: PropTypes.string,
    rightArrow: PropTypes.string
  }),
  /** allows clicking on the next slide to make it an active slide */
  clickToChange: PropTypes.bool,
  /** infinite scroll, will change how slide index is calculated negative when going
   backwards, positive when going forward. Source code does provide a conversion function */
  infinite: PropTypes.bool,
  /** callback with the index as parameter */
  onSlideIndexChange: PropTypes.func,
  partialVisible: PropTypes.bool,
  /** allows you to provide your own paginations */
  pagination: PropTypes.arrayOf(PropTypes.node),
  paginationLocation: PropTypes.oneOf(['top', 'bottom']),
  showArrows: PropTypes.bool,
  showPagination: PropTypes.bool,
  slideIndex: PropTypes.number,
  slidesPerPage: PropTypes.number,
  slidesPerScroll: PropTypes.number,
  // eslint-disable-next-line react/forbid-prop-types
  responsive: PropTypes.object
};
Carousel.defaultProps = {
  centered: false,
  children: null,
  classes: {},
  clickToChange: false,
  infinite: false,
  onSlideIndexChange: () => {},
  partialVisible: false,
  pagination: null,
  paginationLocation: 'bottom',
  showArrows: false,
  showPagination: false,
  slideIndex: 0,
  slidesPerPage: 1,
  slidesPerScroll: 1,
  responsive: null
};
export default Carousel;